import { EnabledTopic, getPractitionerEnabledTopics } from '@/services/timeline-threads';
import {
  CommunicationThreadTopic,
  TIMELINE_FOCUS_ITEM_QUERY_PARAM,
  TIMELINE_CREATE_ITEM_QUERY_PARAM,
  TimelineFilterType,
  Track,
} from '@bariendo/lib/constants';
import { Box, Button, Flex, Paper, Stack, Text } from '@mantine/core';
import { createReference, getReferenceString } from '@medplum/core';
import { Appointment, Coding, Encounter, Practitioner, PractitionerRole, Reference } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useTimelineFilters } from '@/hooks/useTimelineFilters';
import { usePatientTrack } from '@/hooks/usePatientTrack';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'react-h5-audio-player/lib/styles.css';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useAutoScroll } from '../../hooks/useAutoScroll';
import { useCopyDirectLink } from '../../hooks/useCopyDirectLink';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';
import { RecordPaymentModal } from '@/components/RecordPaymentModal/RecordPaymentModal';
import { CherryAccountStatus, SentTreatmentPayment, TextedPaymentLink } from '@/components/Timeline/types';
import { findTextableMobile } from '@/services/payments';
import { usePaymentOrder } from '@/hooks/usePaymentOrder';
import { useRefundRequests } from '@/hooks/useRefundRequests';
import { useBillingTimelineCards } from '@/hooks/useBillingTimelineCards';
import { filterBenefitEventItems, mapBenefitEventsToTimelineItems } from '@/services/Timeline/benefitEventCards';
import { FEATURE_FLAGS } from '@bariendo/lib/constants';
import { useAppointments } from './context-hooks/useAppointments';
import { useCareTeam } from './context-hooks/useCareTeam';
import { useDocuments } from './context-hooks/useDocuments';
import { useMessageBox } from './context-hooks/useMessageBox';
import { usePatient } from './context-hooks/usePatient';
import { useTimeline } from './context-hooks/useTimeline';
import { useMedications } from './context-hooks/useMedications';
import { TimelineContext } from '@/contexts/timelineContexts';
import { PatientInfoPanel } from './PatientInfoPanel';
import { PatientTimelineHeader } from './PatientTimelineHeader/PatientTimelineHeader';
import { TimelineSkeleton } from '../../components/TimelineSkeleton';
import { groupTimelineItems, mapCompletedTasksToTimelineItems } from '@/services/Timeline/timelineGroups';
import { filterCompletedTaskItems } from '@/services/Timeline/timelineFilter';
import { TimelineFilters } from '@/components/Timeline/TimelineFilters';
import { TimelineItems } from '@/components/Timeline/TimelineItems';
import { TimelineMessageBox } from '@/components/Timeline/TimelineMessageBox';
import { alert } from '../../services/alert';
import { fetchBot } from '@/services/services';
import { isGoneError, isNotFoundError, MessageBoxType, TimelineItemType } from '@/components/Timeline/types';
import { useLoadMoreUntilReferenceIsFound } from './hooks/useLoadMoreUntilReferenceIsFound';
import { MedicationIframeModal } from './Medications/MedicationIframeModal';
import { FutureAppointments } from '@/components/Timeline/FutureAppointments';
import { AppointmentDrawer } from '@/pages/SchedulingPage/Appointment/AppointmentDrawer';
import { loadFilterResources } from '@/pages/SchedulingPage/utils/availability';
import { SchedulingPageProvider } from '@/pages/SchedulingPage/SchedulingPageContext';
import type { PractitionerWithRole } from '@/pages/SchedulingPage/types';
import { useAppointmentTypesForScheduling } from './hooks/useAppointmentTypesForScheduling';
import { buildSchedulingContextValue, mapPractitionersToWithRole } from '@/pages/SchedulingPage/utils/schedulingShared';
import { applyPreferredPharmacy } from '@/services/preferred-pharmacy-template';
import { buildDoseSpotDemographicKey } from './Medications/dosespotSyncKey';
import { FilterType } from '@/components/Timeline/TimelineFilters';
import { TimelineGroup } from '@/components/Timeline/types';
import { useScrollPaddingFromStickyHeader } from '@/hooks/useScrollPaddingFromStickyHeader';
import clippedClasses from '@/components/Timeline/clipped.module.css';
import classes from './PatientTimelinePage.module.css';

// Invariant options for the deep-link auto-scroll. Hoisted to module scope so
// the reference is stable across renders — useAutoScroll's effect deps include
// scrollOptions, so an inline object would re-fire the effect every render and
// tear down its retry/visibility plumbing.
const TIMELINE_SCROLL_INTO_VIEW_OPTIONS: ScrollIntoViewOptions = {
  behavior: 'auto',
  block: 'start',
};

// Clearance reserved above a deep-linked timeline item, applied as the right
// panel's scroll-padding-top. It covers two things, not one:
//
//   1. the sticky filter bar, which overlays the top of the panel. Its height
//      is measured rather than assumed — it grows by a row when the
//      provider/date filters are expanded, and a hard-coded sum silently rots
//      the first time that bar changes.
//   2. the head of the card the item lives in. `data-timeline-item` sits on
//      the individual message bubble (HighlightableItem inside
//      TextMessageTimelineItem), which is nested *inside* a TimelineCard whose
//      header carries the date/time. Reserving only the bar's height landed
//      the bubble correctly but left that header above the top of the panel,
//      so a deep link opened onto a card with no visible date/time.
//
// Card head = 1px border + 8px/8px `p="xs"` + ~32px header row + 1px divider +
// 24px `p="lg"`, rounded up for breathing room.
const TIMELINE_CARD_HEAD_CLEARANCE = 96;

// Used when the bar reports no height yet; matches the collapsed bar (16px
// padding + 40px row + 16px padding + 4px). The same total appears once more,
// as the pre-measure default in PatientTimelinePage.module.css — the panel's
// scroll-padding has a single writer (the hook), so the class is the only way
// to cover the frames before the first measurement.
const COLLAPSED_FILTER_BAR_HEIGHT = 76;

interface TimelineContentProps {
  error: unknown;
  hasLoadedInitialData: boolean;
  isWaitingForItems: boolean;
  targetNotFound: boolean;
  showFocusNotFoundMessage: boolean;
  onClearFocusedItem: () => void;
  timelineGroups: TimelineGroup[];
  showAppointments: boolean;
  hasFutureAppointments: boolean;
  activeFilter: FilterType;
  searchTerm: string;
  scrollToRef: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  hasActiveFilters: boolean;
  handleNavigateToItem: (itemReference: string) => void;
  activityCodeSystem: Coding[] | null;
  onAppointmentStatusChange: (updatedAppointment: Appointment) => void;
  hasMore: boolean;
  loadMoreSentinelRef: { current: HTMLDivElement | null };
}

function getTimelineEmptyStateMessage(activeFilter: FilterType, searchTerm: string): string {
  if (activeFilter === TimelineFilterType.APPOINTMENT) {
    return searchTerm ? 'No appointments found matching your search' : 'No appointments found for this patient';
  }

  if (activeFilter === 'encounter') {
    return searchTerm ? 'No encounters found matching your search' : 'No encounters found for this patient';
  }

  if (activeFilter === TimelineFilterType.BILLING) {
    return 'No billing activity for this patient';
  }

  return searchTerm ? 'No communications found matching your search' : 'No communications found for this patient';
}

function TimelineContent(props: TimelineContentProps): ReactElement {
  const {
    error,
    hasLoadedInitialData,
    isWaitingForItems,
    targetNotFound,
    showFocusNotFoundMessage,
    onClearFocusedItem,
    timelineGroups,
    showAppointments,
    hasFutureAppointments,
    activeFilter,
    searchTerm,
    scrollToRef,
    copyDirectLink,
    hasActiveFilters,
    handleNavigateToItem,
    activityCodeSystem,
    onAppointmentStatusChange,
    hasMore,
    loadMoreSentinelRef,
  } = props;

  if (error) {
    return (
      <Paper p="xl" ta="center">
        <Text c="red">Couldn't load timeline</Text>
      </Paper>
    );
  }

  if (!hasLoadedInitialData || isWaitingForItems || (targetNotFound && !showFocusNotFoundMessage)) {
    return <TimelineSkeleton />;
  }

  if (targetNotFound) {
    return (
      <Paper p="xl" ta="center">
        <Stack align="center" gap="md">
          <Text c="dimmed">
            Couldn't find the selected timeline item, the link might be invalid or you might not have access to the
            selected item
          </Text>
          <Button variant="white" c="#228be6" onClick={onClearFocusedItem}>
            Clear selection
          </Button>
        </Stack>
      </Paper>
    );
  }

  if (timelineGroups.length === 0 && !(showAppointments && hasFutureAppointments)) {
    return (
      <Paper p="xl" ta="center">
        <Text c="dimmed">{getTimelineEmptyStateMessage(activeFilter, searchTerm)}</Text>
      </Paper>
    );
  }

  return (
    <Stack gap="0">
      <TimelineItems
        timelineGroups={timelineGroups}
        highlightRef={scrollToRef}
        copyDirectLink={copyDirectLink}
        hasActiveFilters={hasActiveFilters}
        handleNavigateToItem={handleNavigateToItem}
        searchTerm={searchTerm}
        activityCodeSystem={activityCodeSystem ?? []}
        onAppointmentStatusChange={onAppointmentStatusChange}
      />

      {hasMore && <div ref={loadMoreSentinelRef} style={{ height: 1 }} />}
    </Stack>
  );
}

export function PatientTimelinePage(): ReactElement {
  const navigate = useNavigate();
  const currentUser = useMedplumProfile();
  const medplum = useMedplum();
  const { currentUserPermissions } = useCurrentUser();

  // Only show medications modal if user has medication write permission
  const canWriteMedications = currentUserPermissions.canWriteMedications;

  const { patientId } = useParams<{ patientId: string }>();
  const patientContext = usePatient(patientId);
  const { patient, patientError, patientLoading } = patientContext;

  // Passing `undefined` when the viewer cannot see payments is what stops the
  // fetch: the bot would otherwise log a refused-permission warning on every
  // patient view opened by anyone outside PAYMENTS_ROLES.
  // The flag hook is called unconditionally — short-circuiting it behind the
  // permission would make the hook order depend on a role.
  const treatmentPaymentsEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_TREATMENT_PAYMENTS);
  const canSeePayments = currentUserPermissions.canViewPayments && treatmentPaymentsEnabled;
  // Finance sees the order and ledger but takes no payments (BAR-2648), so every
  // action below keys on this, not on canSeePayments.
  const canTakePayments = currentUserPermissions.canAccessPayments && treatmentPaymentsEnabled;
  // One instance, shared with the drawer via PatientTimelineHeader. The card and
  // the drawer must read the same state or creating an order leaves the card stale.
  const paymentOrder = usePaymentOrder(canSeePayments ? patient : undefined);
  const [isPaymentOrderOpened, setIsPaymentOrderOpened] = useState(false);
  const [isRecordPaymentOpened, setIsRecordPaymentOpened] = useState(false);
  // Refund requests (BAR-3052), read once for the page: the + Add menu and the refund composer
  // need them under every filter, the timeline cards under All and Billing (BAR-3069).
  const refundRequests = useRefundRequests(canSeePayments ? patient : undefined);
  const benefitEventsEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_EMPLOYER_BENEFIT_EVENTS);
  // Insurance cards word Approved differently (BAR-3078).
  const { track } = usePatientTrack(patient?.id);

  // Defined here rather than in the card so the card stays a pure render of
  // state it was handed, and so the refresh hits the page's single
  // usePaymentOrder instance — the new payment leg has to land on the card.
  const paymentOrderRefresh = paymentOrder.refresh;
  const sendPaymentLink = useCallback(
    async (input: { paymentProcessor: string; practiceId?: string; amount?: number }) => {
      if (!patient) {
        throw new Error('No patient loaded');
      }

      const result = await fetchBot<{ payment: SentTreatmentPayment }>(medplum, 'create-treatment-payment', {
        operation: 'sendLink',
        patient: createReference(patient),
        paymentProcessor: input.paymentProcessor,
        practiceId: input.practiceId,
        amount: input.amount,
      });

      await paymentOrderRefresh();

      return result.payment;
    },
    [medplum, patient, paymentOrderRefresh]
  );

  // Asked when a rep picks the Cherry rail, never on page load: it reaches
  // Cherry through bariendo-be and Cherry rate-limits the lookup, so running it
  // per timeline view would spend that budget on patients nobody is charging.
  const cherryStatus = useCallback(async (): Promise<CherryAccountStatus> => {
    if (!patient) {
      throw new Error('No patient loaded');
    }

    const result = await fetchBot<{ status: CherryAccountStatus }>(medplum, 'create-treatment-payment', {
      operation: 'cherryStatus',
      patient: createReference(patient),
    });

    return result.status;
  }, [medplum, patient]);

  // Only offered when the patient's number is already E.164 — see
  // `findTextableMobile`. Resolving it here rather than in the card is what lets
  // the card treat "no callback" as "not possible", the same way it treats the
  // payments permission.
  const textablePhone = patient ? findTextableMobile(patient) : undefined;
  const orderServiceName = paymentOrder.order?.items.find((line) => line.serviceName)?.serviceName;
  const textPaymentLink = useCallback(
    async (paymentLink: string): Promise<TextedPaymentLink> => {
      if (!patient || !textablePhone) {
        throw new Error('No textable phone number for this patient');
      }

      const firstName = patient.name?.[0]?.given?.[0];
      const forService = orderServiceName ? ` for ${orderServiceName}` : '';
      const message = `${firstName ? `Hi ${firstName}, h` : 'H'}ere's your secure payment link${forService}: ${paymentLink}`;

      const communications = await fetchBot<{ sent?: string }[]>(medplum, 'send-sms-message', {
        phoneNumber: textablePhone,
        message,
        recipient: getReferenceString(patient),
      });

      // The Communication's own `sent` rather than our clock: it is the time the
      // record on the timeline carries, and the card must not disagree with it.
      return {
        sentAt: communications?.[0]?.sent ?? new Date().toISOString(),
        toLast4: textablePhone.slice(-4),
      };
    },
    [medplum, patient, textablePhone, orderServiceName]
  );

  const careTeamContext = useCareTeam(patient);
  const messageBoxContext = useMessageBox();
  const { sentMessage, setMessageBoxVariant, setShowMessageInput, setEditingEncounterReference } = messageBoxContext;

  const documentsContext = useDocuments(patient);

  // Single source of truth for procedure appointments. Both the header
  // (per-offering pill) and the left-panel Treatment section consume from
  // here — replaces two parallel useAppointments() round-trips per page load.
  const appointmentsContext = useAppointments(patient);

  const [searchParams, setSearchParams] = useSearchParams();
  const scrollToRef = searchParams.get(TIMELINE_FOCUS_ITEM_QUERY_PARAM);

  const medicationsContext = useMedications();

  const timelineContext = useTimeline({
    patient,
    urlSearchParams: searchParams,
  });

  const [enabledTopics, setEnabledTopics] = useState<EnabledTopic[]>([]);
  const [appointmentDrawerOpened, setAppointmentDrawerOpened] = useState(false);
  const [appointmentPractitioners, setAppointmentPractitioners] = useState<Practitioner[]>([]);
  const [appointmentPractitionerRoles, setAppointmentPractitionerRoles] = useState<PractitionerRole[]>([]);

  useEffect(() => {
    if (!patientLoading && patientError) {
      switch (true) {
        case isNotFoundError(patientError):
          alert.error('Patient not found or no longer exists.');
          break;
        case isGoneError(patientError):
          alert.error('Patient no longer exists.');
          break;
        default:
          alert.error('An error occurred while loading the patient.');
      }
    }
  }, [patientError, patientLoading, navigate]);

  const {
    activeFilter,
    completedClinicalTasks,
    searchTerm,
    startDate,
    endDate,
    selectedProvider,
    filteredTimelineItems,
    filteredTimelineNotes,
    error,
    loading,
    loadingMore,
    hasMore,
    hasLoadedInitialData,
    encounters,
    diagnosticReports,
    prescriptionOrders,
    pastAppointments,
    futureAppointments,
    eligibilityForms,
    loadMore,
    refresh,
  } = timelineContext;

  // Under All and Billing only, and never without canViewPayments (BAR-2646).
  const {
    paymentCards,
    refundCards,
    benefitEvents,
    loading: billingLoading,
  } = useBillingTimelineCards({
    patient,
    activeFilter,
    canViewPayments: currentUserPermissions.canViewPayments,
    treatmentPaymentsEnabled,
    benefitEventsEnabled,
    paymentOrderCards: paymentOrder.cards,
    paymentOrderLoading: paymentOrder.loading,
    refundRequests: canSeePayments ? refundRequests : undefined,
  });

  // Shown under All and the Notes filter only, and subject to the same
  // search / date-range / provider filters as every other timeline row.
  const filteredCompletedTasks = useMemo(() => {
    if (activeFilter !== TimelineFilterType.ALL && activeFilter !== TimelineFilterType.DOCUMENTATION) {
      return [];
    }
    return filterCompletedTaskItems(
      mapCompletedTasksToTimelineItems(completedClinicalTasks),
      searchTerm,
      selectedProvider,
      startDate,
      endDate
    );
  }, [completedClinicalTasks, activeFilter, searchTerm, selectedProvider, startDate, endDate]);

  const filteredBenefitEvents = useMemo(
    () =>
      filterBenefitEventItems(
        mapBenefitEventsToTimelineItems(benefitEvents.resources, {
          insurance: track === Track.INSURANCE,
          prepTasks: benefitEvents.prepTasks,
        }),
        activeFilter,
        selectedProvider,
        startDate,
        endDate
      ),
    [benefitEvents.resources, benefitEvents.prepTasks, track, activeFilter, selectedProvider, startDate, endDate]
  );

  useEffect(() => {
    const fetchtEnabledTopics = async () => {
      if (patient) {
        const enabled = await getPractitionerEnabledTopics(medplum, patient);
        setEnabledTopics(enabled);
      }
    };
    fetchtEnabledTopics();
  }, [medplum, patient]);

  useEffect(() => {
    const loadPractitioners = async () => {
      try {
        const resources = await loadFilterResources(medplum);
        setAppointmentPractitioners(resources.practitioners);
        setAppointmentPractitionerRoles(resources.practitionerRoles);
      } catch (error) {
        alert.error(
          "Couldn't load providers: " +
            (error instanceof Error ? error.message : 'An error occurred while loading providers')
        );
      }
    };

    loadPractitioners();
  }, [medplum]);

  const handleDoseSpotSyncSuccess = useCallback(() => {
    if (!patient?.id || !canWriteMedications) {
      return;
    }

    void applyPreferredPharmacy(medplum, { patientId: patient.id });
  }, [medplum, patient?.id, canWriteMedications]);

  const doseSpotDemographicKey = useMemo(() => buildDoseSpotDemographicKey(patient), [patient]);

  const { getActivityDefinitionForAppointmentType, activityCodeSystem } = useAppointmentTypesForScheduling();

  const appointmentPractitionersWithRole: PractitionerWithRole[] = useMemo(
    () => mapPractitionersToWithRole(appointmentPractitioners, appointmentPractitionerRoles),
    [appointmentPractitioners, appointmentPractitionerRoles]
  );

  const schedulingContextValue = useMemo(
    () => buildSchedulingContextValue(activityCodeSystem, getActivityDefinitionForAppointmentType),
    [activityCodeSystem, getActivityDefinitionForAppointmentType]
  );

  const { copyDirectLink } = useCopyDirectLink({
    paramName: TIMELINE_FOCUS_ITEM_QUERY_PARAM,
    errorTitle: "Couldn't copy link",
    errorMessage: "We couldn't copy the direct link to your clipboard.",
    trackingEnabled: true,
    getItemType: (itemReference: string) => {
      const item = filteredTimelineItems.find((i) => i.reference === itemReference);
      if (!item) return 'unknown';
      const parts = item?.type.split('-');
      const last = parts[parts.length - 1];
      if (last === 'fax') return 'fax';
      return last as 'chat' | 'sms' | 'call' | 'note';
    },
    patientId,
  });

  const handleNavigateToItem = useCallback(
    (itemReference: string) => {
      const currentUrl = new URL(window.location.href);
      const newSearchParams = new URLSearchParams();
      newSearchParams.set(TIMELINE_FOCUS_ITEM_QUERY_PARAM, itemReference);
      navigate(currentUrl.pathname + '?' + newSearchParams.toString());
    },
    [navigate]
  );

  const handleEditEncounter = useCallback(
    (encounterReference: Reference<Encounter>) => {
      // Clear the focus query parameter when editing an encounter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(TIMELINE_FOCUS_ITEM_QUERY_PARAM);
      setSearchParams(newSearchParams);
      setMessageBoxVariant(MessageBoxType.ENCOUNTER);
      setEditingEncounterReference(encounterReference);
      setShowMessageInput(true);
    },
    [setEditingEncounterReference, setMessageBoxVariant, setShowMessageInput, searchParams, setSearchParams]
  );

  const handleAppointmentStatusChange = useCallback(
    (_updatedAppointment: Appointment) => {
      refresh();
    },
    [refresh]
  );

  const filterHandlers = useTimelineFilters({
    activeFilter,
    searchTerm,
    startDate,
    endDate,
    selectedProvider,
  });

  const showAppointments = useMemo(() => {
    return activeFilter === TimelineFilterType.ALL || activeFilter === TimelineFilterType.APPOINTMENT;
  }, [activeFilter]);

  const timelineGroups = useMemo(() => {
    const allGroups = groupTimelineItems({
      completedTasks: filteredCompletedTasks,
      timelineRegisters: filteredTimelineItems,
      timelineNotes: filteredTimelineNotes,
      encounters,
      diagnosticReports,
      prescriptionOrders,
      appointments: showAppointments ? pastAppointments : [],
      eligibilityForms,
      paymentItems: paymentCards,
      benefitEvents: filteredBenefitEvents,
      refundEvents: refundCards,
    });

    // Filter out chat groups with unknown thread topics
    return allGroups.filter((group) => {
      if (group.type !== TimelineItemType.CHAT) return true;

      const firstItem = group.items[0];
      const threadTopic = firstItem?.resource?.partOf?.[0]?.resource?.topic?.coding?.[0]?.code;

      // Only show chat groups with known thread topics
      return (
        threadTopic === CommunicationThreadTopic.CLINICAL ||
        threadTopic === CommunicationThreadTopic.LOGISTICAL ||
        threadTopic === CommunicationThreadTopic.NUTRITION
      );
    });
  }, [
    filteredCompletedTasks,
    filteredTimelineItems,
    filteredTimelineNotes,
    encounters,
    diagnosticReports,
    prescriptionOrders,
    pastAppointments,
    showAppointments,
    eligibilityForms,
    paymentCards,
    filteredBenefitEvents,
    refundCards,
  ]);

  const { isWaitingForItems, targetNotFound } = useLoadMoreUntilReferenceIsFound({
    hasLoadedInitialData,
    loading: loading || loadingMore,
    hasMore,
    timelineGroups,
    medplum,
    loadMore,
    scrollToRef,
  });
  const [showFocusNotFoundMessage, setShowFocusNotFoundMessage] = useState(false);

  useEffect(() => {
    if (!targetNotFound) {
      setShowFocusNotFoundMessage(false);
      return;
    }

    // Avoid flashing the not-found panel while pagination is still settling.
    // If the item appears quickly, this timeout is cleared and no message is shown.
    const timeoutId = window.setTimeout(() => {
      setShowFocusNotFoundMessage(true);
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [targetNotFound]);

  // Set focus on the last message if it's from the current user
  useEffect(() => {
    if (!timelineGroups.length || loading || !hasLoadedInitialData) return;

    const lastGroup = timelineGroups[0];
    // Skip prescription orders and appointments as they don't have sender info
    if (lastGroup.type === TimelineItemType.PRESCRIPTION_ORDER || lastGroup.type === TimelineItemType.APPOINTMENT) {
      return;
    }

    const lastMessage = lastGroup.items[0];
    if (
      'sender' in lastMessage &&
      lastMessage.sender &&
      getReferenceString(lastMessage.sender as Practitioner) === getReferenceString(currentUser!) &&
      sentMessage
    ) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set(TIMELINE_FOCUS_ITEM_QUERY_PARAM, getReferenceString(lastMessage.reference)!);
        return newParams;
      });
    }
  }, [timelineGroups, currentUser, setSearchParams, sentMessage, loading, hasLoadedInitialData]);

  // Open the encounter creation box if receive the query param to create a new encounter
  useEffect(() => {
    const action = searchParams.get(TIMELINE_CREATE_ITEM_QUERY_PARAM);
    const appointment = searchParams.get('appointment');

    if (action === 'chart' && appointment) {
      setMessageBoxVariant(MessageBoxType.ENCOUNTER);
      setShowMessageInput(true);
    }
  }, [searchParams, setMessageBoxVariant, setShowMessageInput]);

  const handleTimelineScrollTargetNotFound = useCallback(() => {
    alert.error(
      "Couldn't find the selected timeline item, the link might be invalid or you might not have access to the selected item"
    );
  }, []);

  const handleClearFocusedItem = useCallback(() => {
    setSearchParams((prev) => {
      const nextParams = new URLSearchParams(prev);
      nextParams.delete(TIMELINE_FOCUS_ITEM_QUERY_PARAM);
      return nextParams;
    });
  }, [setSearchParams]);

  useAutoScroll({
    retryAttempts: 10,
    retryDelay: 300,
    targetRef: scrollToRef,
    isReady: !isWaitingForItems,
    dataAttribute: 'data-timeline-item',
    onTargetNotFound: handleTimelineScrollTargetNotFound,
    // block: 'start' bypasses useAutoScroll's viewport-aware center helper
    // (which miscomputes alignment when the scroll container is the panel,
    // not the document). The right panel's scrollPaddingTop, measured from the
    // sticky filter bar, offsets the landing position below that bar and clear
    // of the target card's own header.
    scrollOptions: TIMELINE_SCROLL_INTO_VIEW_OPTIONS,
  });

  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const filterBarRef = useRef<HTMLDivElement>(null);

  // Keep the panel's scroll-padding in step with the sticky bar's real height,
  // so a deep-linked item lands below the bar and below its own card's header
  // whether or not the provider/date filters are expanded.
  useScrollPaddingFromStickyHeader({
    headerRef: filterBarRef,
    scrollContainerRef: rightPanelRef,
    extraClearance: TIMELINE_CARD_HEAD_CLEARANCE,
    fallbackHeaderHeight: COLLAPSED_FILTER_BAR_HEIGHT,
  });

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '200px', root: rightPanelRef.current }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadMore]);

  return (
    <TimelineContext.Provider
      value={{
        patientContext,
        careTeamContext,
        appointmentsContext,
        // Already gated: usePaymentOrder above is passed `undefined` for a
        // viewer outside PAYMENTS_VIEW_ROLES, so this carries no order for them.
        paymentOrderContext: paymentOrder,
        refundRequestsContext: canSeePayments ? refundRequests : undefined,
        timelineContext,
        documentsContext,
        messageBoxContext,
        medicationsContext,
        onEditEncounter: handleEditEncounter,
        onCoverageActivityRecorded: benefitEvents.refresh,
        // Withheld entirely for a viewer who cannot take payments, so the card
        // shows the pivot as unavailable rather than failing on click.
        onOpenPaymentOrder: canTakePayments ? () => setIsPaymentOrderOpened(true) : undefined,
        onSendPaymentLink: canTakePayments && patient ? sendPaymentLink : undefined,
        onRecordPayment: canTakePayments && patient ? () => setIsRecordPaymentOpened(true) : undefined,
        onTextPaymentLink: canTakePayments && patient && textablePhone ? textPaymentLink : undefined,
        textablePhone: canTakePayments ? textablePhone : undefined,
        onCherryStatus: canTakePayments && patient ? cherryStatus : undefined,
      }}
    >
      <Flex
        direction="column"
        // clipped, not overflow: hidden — a hidden box is silently scrollable
        // and this one has no scrollbar at all. See clipped.module.css.
        className={clippedClasses.clipped}
        style={{
          height: 'calc(100dvh - var(--app-shell-header-height, 0px))',
        }}
      >
        <PatientTimelineHeader
          patient={patient}
          enabledTopics={enabledTopics}
          onAddAppointment={() => setAppointmentDrawerOpened(true)}
          paymentOrder={paymentOrder}
          isPaymentOrderOpened={isPaymentOrderOpened}
          setIsPaymentOrderOpened={setIsPaymentOrderOpened}
        />
        {/* Rendered here rather than beside the card: the card is one of many in
            a virtualised timeline, and the refresh has to reach the page's single
            usePaymentOrder instance — same reasoning as `sendPaymentLink`. */}
        {patient && (
          <RecordPaymentModal
            patient={patient}
            opened={isRecordPaymentOpened}
            onClose={() => setIsRecordPaymentOpened(false)}
            onRecorded={paymentOrderRefresh}
          />
        )}
        <Flex direction={{ base: 'column', xs: 'row' }} align="stretch" wrap="nowrap" flex="1" mih={0}>
          <Stack mih={0} px="md" maw={{ xs: '380px' }} w={{ base: '100%', xs: '28%' }} style={{ overflowY: 'auto' }}>
            <PatientInfoPanel />
          </Stack>

          <Stack
            ref={rightPanelRef}
            mih={0}
            className={classes.timelinePanel}
            style={{ flex: 1, overflowY: 'auto' }}
            bg="gray.1"
          >
            <Box
              ref={filterBarRef}
              pos="sticky"
              top={0}
              mb={12}
              pb={4}
              style={{
                zIndex: 20,
                backgroundColor: 'var(--mantine-color-gray-1)',
              }}
            >
              <TimelineFilters
                activeFilter={activeFilter}
                onFilterChange={filterHandlers.handleFilterChange}
                searchTerm={filterHandlers.localSearchTerm}
                onSearchChange={filterHandlers.handleSearchChange}
                selectedProvider={selectedProvider}
                onProviderChange={filterHandlers.handleProviderChange}
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={filterHandlers.handleStartDateChange}
                onEndDateChange={filterHandlers.handleEndDateChange}
                clinical={currentUserPermissions.canViewClinicalRecord}
                billing={currentUserPermissions.canViewPayments}
              />
            </Box>
            <TimelineMessageBox enabledTopics={enabledTopics} handleNavigateToItem={handleNavigateToItem} />

            {showAppointments && futureAppointments.length > 0 && (
              <FutureAppointments
                futureAppointments={futureAppointments}
                onAppointmentStatusChange={handleAppointmentStatusChange}
              />
            )}

            <TimelineContent
              error={error}
              // Billing's cards come from the payments bot and the benefit search, not the
              // streams behind hasLoadedInitialData; wait for them before saying "none".
              hasLoadedInitialData={
                hasLoadedInitialData && !(activeFilter === TimelineFilterType.BILLING && billingLoading)
              }
              isWaitingForItems={isWaitingForItems}
              targetNotFound={targetNotFound}
              showFocusNotFoundMessage={showFocusNotFoundMessage}
              onClearFocusedItem={handleClearFocusedItem}
              timelineGroups={timelineGroups}
              showAppointments={showAppointments}
              hasFutureAppointments={futureAppointments.length > 0}
              activeFilter={activeFilter}
              searchTerm={searchTerm}
              scrollToRef={scrollToRef}
              copyDirectLink={copyDirectLink}
              hasActiveFilters={filterHandlers.hasActiveFilters}
              handleNavigateToItem={handleNavigateToItem}
              activityCodeSystem={activityCodeSystem}
              onAppointmentStatusChange={handleAppointmentStatusChange}
              hasMore={hasMore}
              loadMoreSentinelRef={loadMoreSentinelRef}
            />
          </Stack>
        </Flex>
      </Flex>

      <SchedulingPageProvider value={schedulingContextValue}>
        <AppointmentDrawer
          opened={appointmentDrawerOpened}
          onClose={() => setAppointmentDrawerOpened(false)}
          practitioners={appointmentPractitionersWithRole}
          appointmentReference={null}
          initialGuests={patient ? [patient] : []}
        />
      </SchedulingPageProvider>

      {canWriteMedications && (
        <MedicationIframeModal
          key={`${patientId}:${doseSpotDemographicKey}`}
          patientId={patientId}
          onDoseSpotSyncSuccess={handleDoseSpotSyncSuccess}
        />
      )}
    </TimelineContext.Provider>
  );
}
